/**
 * /forgot-password: ask for a reset link. The answer is the same whether an
 * account exists or not, so the page always shows the same confirmation.
 * Same frame as /Login (AuthCard, « Retour au site »), active button and
 * validation at submit (B3-forgot-password).
 */
import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { requestPasswordReset } from '@/api/endpoints/password';
import { ApiErrorException } from '@/api/client';
import { passwordApiErrorKey } from '@/lib/passwordRules';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { AuthCard, AUTH_BUTTON_CLASS, AUTH_INPUT_CLASS, FieldError, emailFieldError } from './auth/AuthCard';

export default function ForgotPassword() {
  const { t } = useTranslation(['admin', 'common']);
  useDocumentTitle(t('admin:passwordReset.requestTitle'));
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const emailRef = useRef<HTMLInputElement>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const fieldError = emailFieldError(email);
    setEmailError(fieldError);
    setError('');
    if (fieldError) { emailRef.current?.focus(); return; }
    setLoading(true);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      const apiErr = err instanceof ApiErrorException ? err : null;
      setError(t(passwordApiErrorKey(apiErr?.code, apiErr?.status)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard title={t('admin:passwordReset.requestTitle')} subtitle={t('admin:passwordReset.requestSubtitle')}>
      {sent ? (
        <p className="text-white/80 text-sm" role="status">{t('admin:passwordReset.sent')}</p>
      ) : (
        <form onSubmit={submit} noValidate className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="forgot-email" className="text-white/80">{t('admin:passwordReset.emailLabel')}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" aria-hidden />
              <Input
                id="forgot-email"
                ref={emailRef}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('admin:auth.emailPlaceholder')}
                className={AUTH_INPUT_CLASS}
                aria-invalid={emailError ? true : undefined}
                aria-describedby={emailError ? 'forgot-email-error' : undefined}
                autoFocus
                autoComplete="email"
              />
            </div>
            <FieldError id="forgot-email-error" message={emailError && t(emailError)} />
          </div>
          {error && <p className="text-red-400 text-sm" role="alert">{error}</p>}
          <Button type="submit" disabled={loading} aria-busy={loading} className={AUTH_BUTTON_CLASS}>
            {/* REV-W6-F5: the label stays for screen readers while the spinner turns. */}
            {loading && <Loader2 className="w-5 h-5 animate-spin motion-reduce:animate-none" aria-hidden />}
            <span className={loading ? 'sr-only' : undefined}>{t('admin:passwordReset.send')}</span>
          </Button>
        </form>
      )}
      <Link to="/Login" className="mt-6 inline-flex min-h-[44px] items-center gap-2 text-sm text-white/60 hover:text-white">
        <ArrowLeft className="w-4 h-4" aria-hidden />
        {t('admin:passwordReset.backToLogin')}
      </Link>
    </AuthCard>
  );
}
